import { batch } from 'react-redux';
import { AbortedError } from 'src/common/api/extended-emerald-error';
import { clearEmeraldModals } from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { delay } from 'src/common/helpers/delay-helper';
import XLRLogger from 'src/common/helpers/logger';
import { IMessageModal } from 'src/common/interfaces/dtos/IMessageModal';
import { PaymentUsage, RefundUsage } from 'src/common/interfaces/dtos/IPreTenderAdd';
import { EpsData, SignatureDetails } from 'src/devices/payment/dtos/eps-data';
import {
  getSignatureFromPinPad,
  signatureNotVerified,
} from 'src/devices/payment/pinpad-operations';
import { getPaymentTenderOptions } from 'src/home-page/business-logic/get-payment-options';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import { store } from '../../common/redux/configure-store';
import { TenderingActions } from '../../common/redux/reducers/tendering-reducer';
import { shiftToTenderingView } from '../components/process-tender/tender-processing-view';
import { showTenderSignatureView } from '../components/signature/checkout-signature';
import { BuildLineTenderProps } from './build-line-tender';
import { showNotification } from './show-notification';

export const processSignature = async (
  lineTender: BuildLineTenderProps,
  epsData: EpsData,
): Promise<void> => {
  let signatureVerificationPolicy = lineTender.tender?.epsSignatureVerificationPolicy;

  if (!signatureVerificationPolicy) {
    await getPaymentTenderOptions(lineTender.tender?.id)
      .then(option => {
        signatureVerificationPolicy = option.signatureVerificationPolicy;
      })
      .catch(error => {
        XLRLogger.error({
          message: 'Error retrieving full tender details',
          source: 'handle-tender-selection',
          data: error,
        });
      });
  }

  const isRefund =
    lineTender.usage === RefundUsage.Refund || lineTender.usage === PaymentUsage.ExchangeReceive;

  if (!isRefund) {
    store.dispatch(
      TenderingActions.setPaymentInfoText('EFTTenderCardApprovedWaitingOnSignturePrompt'),
    );
  } else {
    store.dispatch(
      TenderingActions.setPaymentInfoText('EFTTenderRefundApprovedWaitForInputPrompt'),
    );
  }

  // The delay is a work around fix for a race between the purchase and
  // getSignature calls to the pinpad. Without the delay, it is likely that the
  // getSignature will throw a cancel command ending the transaction.
  await delay(3000);

  await collectCustomerSignature(epsData.paymentTranID, signatureVerificationPolicy).catch(err => {
    if (err instanceof AbortedError) {
      if (signatureVerificationPolicy === 'Verification') {
        store.dispatch(TenderingActions.setIsProcessingSignature(false));
        throw new AbortedError('SignatureRejected');
      }
    } else {
      throw err;
    }
  });

  if (!isRefund) {
    store.dispatch(TenderingActions.setPaymentInfoText('EFTTenderCardApprovedPrompt'));
  } else {
    store.dispatch(TenderingActions.setPaymentInfoText('EFTTenderRefundApprovedComplete'));
  }
};

export const collectCustomerSignature = async (
  paymentTranID: string,
  signatureVerificationPolicy?: string,
): Promise<void> => {
  const signature: SignatureDetails = await getSignatureFromPinPad(paymentTranID);

  switch (signatureVerificationPolicy) {
    case 'Verification':
      return getSignatureVerification(signature);
    case 'Acknowledgment':
      return getSignatureAcknowledgment();
  }
};

export const getNotificationReSign = async (): Promise<void> => {
  await showNotification('SignatureNotificationReSignTitle', 'SignatureNotificationReSignMessage');
};

export const getSignatureAcknowledgment = async (): Promise<void> => {
  const { eftSignatureVerificationDialog } = getCurrentXLRConfig();
  if (eftSignatureVerificationDialog) {
    await showNotification('Manual_Card_Entry_Command', 'EPS_VerifySignature');
  } else {
    await showNotification('SignatureAcknowledgmentTitle', 'SignatureAcknowledgmentMessage');
  }
};

export const getSignatureVerification = async (signature?: SignatureDetails): Promise<void> => {
  const { eftSignatureVerificationDialog } = getCurrentXLRConfig();

  batch(() => {
    store.dispatch(TenderingActions.setIsProcessingSignature(true));
  });

  if (!eftSignatureVerificationDialog) {
    return showTenderSignatureView(signature?.imageData)
      .then(shiftToTenderingView)
      .catch(rejectSignature);
  } else {
    return popupSignatureVerification().then(shiftToTenderingView);
  }
};

export const popupSignatureVerification = (): Promise<void> =>
  new Promise((resolve, reject) => {
    const modal: IMessageModal = {
      message: 'Manual_Card_Entry_Command',
      subMessage: 'EPS_VerifySignature',
      acceptButtonLabel: 'Yes_CommandName',
      closeButtonLabel: 'No_CommandName',
      separateButton: true,
      onAccept: () => {
        clearEmeraldModals();
        resolve();
      },
      onCancel: async () => {
        try {
          await signatureNotVerified();
          resolve();
        } catch (err) {
          reject(err);
        } finally {
          clearEmeraldModals();
        }
      },
    };

    popupMessage(modal);
  });

export const rejectSignature = async (): Promise<void> => {
  await showNotification('RejectSignatureTitle', 'RejectSignatureMessage');
  throw new AbortedError();
};
